import { ApiResponse } from '@bloom/shared';

// Types for payment API responses
export interface PaymentLinkData {
  orderId: string;
  patientName: string;
  clinicName: string;
  items: Array<{
    productName: string;
    quantity: number;
    unitPrice: number;
    totalPrice: number;
  }>;
  totalAmount: number;
  currency: string;
  paymentStatus: 'pending' | 'processing' | 'succeeded' | 'failed';
  expiresAt?: string;
}

export interface HyperswitchConfig {
  publishableKey: string;
  clientSecret: string;
  appearance?: {
    theme: 'stripe' | 'night' | 'flat';
    variables?: Record<string, string>;
  };
}

export interface PaymentLinkResponse {
  paymentData: PaymentLinkData;
  hyperswitchConfig: HyperswitchConfig;
}

export interface PaymentInitiationRequest {
  orderId: string;
  paymentMethod: 'patient_pay';
}

export interface PaymentInitiationResponse {
  success: boolean;
  paymentLink?: string;
  paymentLinkId?: string;
  customerId?: string;
  paymentIntentId?: string;
  error?: string;
}

export interface PaymentStatusResponse {
  orderId: string;
  status: 'pending' | 'processing' | 'succeeded' | 'failed';
  paymentIntentId?: string;
  transactionId?: string;
  failureReason?: string;
  paidAt?: string;
  amount?: number;
  currency?: string;
}

export interface PaymentSuccessData {
  orderId: string;
  paymentIntentId: string;
  transactionId: string;
  patientName: string;
  clinicName: string;
  clinicPhone?: string;
  clinicEmail?: string;
  items: Array<{
    productName: string;
    quantity: number;
    totalPrice: number;
  }>;
  totalAmount: number;
  currency: string;
  paymentMethod: {
    type: string;
    last4?: string;
    brand?: string;
  };
  paidAt: string;
  estimatedFulfillmentDate?: string;
}

export interface PaymentFailureData {
  orderId: string;
  paymentLinkId: string;
  patientName: string;
  clinicName: string;
  clinicPhone?: string;
  clinicEmail?: string;
  totalAmount: number;
  currency: string;
  failureReason: string;
  errorCode?: string;
  canRetry: boolean;
  retryLimit: number;
  attemptCount: number;
  failedAt: string;
}

/**
 * Payment API service for handling patient payment operations
 */
export class PaymentApiService {
  private baseURL: string;

  constructor() {
    this.baseURL = import.meta.env.VITE_API_URL || '/api';
  }

  private getAuthHeaders(): Record<string, string> {
    const token = localStorage.getItem('bloom_token');
    return {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` })
    };
  }

  private async makeRequest<T>(
    endpoint: string, 
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    try {
      const response = await fetch(`${this.baseURL}${endpoint}`, {
        headers: this.getAuthHeaders(),
        ...options,
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          error: data.error || `HTTP ${response.status}: ${response.statusText}`,
          data: null as any
        };
      }

      return {
        success: true,
        data: data.data || data,
        error: undefined
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Network error',
        data: null as any
      };
    }
  }

  /**
   * Create payment intent for an order (Physician/Staff action)
   */
  async createPaymentIntent(orderId: string, patientId: string): Promise<ApiResponse<any>> {
    return this.makeRequest<any>(`/payments/orders/${orderId}/intent`, {
      method: 'POST',
      body: JSON.stringify({ patientId })
    });
  }

  /**
   * Generate payment link for an order (Physician/Staff action)
   */
  async generatePaymentLink(orderId: string): Promise<ApiResponse<any>> {
    return this.makeRequest<any>(`/payments/orders/${orderId}/link`, {
      method: 'POST',
      body: JSON.stringify({})
    });
  }

  /**
   * Get payment link details (Patient action - no auth required)
   */
  async getPaymentLinkDetails(paymentLinkId: string): Promise<ApiResponse<any>> {
    // Make request without auth headers for public endpoint
    try {
      const response = await fetch(`${this.baseURL}/payments/link/${paymentLinkId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          error: data.error || `HTTP ${response.status}: ${response.statusText}`,
          data: null as any
        };
      }

      return {
        success: true,
        data: data.data || data,
        error: undefined
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Network error',
        data: null as any
      };
    }
  }

  /**
   * Process payment for a payment link (Patient action - no auth required)
   */
  async processPayment(paymentLinkId: string, paymentData: {
    paymentMethodId: string;
    billingAddress: any;
  }): Promise<ApiResponse<any>> {
    // Make request without auth headers for public endpoint
    try {
      const response = await fetch(`${this.baseURL}/payments/link/${paymentLinkId}/process`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(paymentData)
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          error: data.error || `HTTP ${response.status}: ${response.statusText}`,
          data: null as any
        };
      }

      return {
        success: true,
        data: data.data || data,
        error: undefined
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Network error',
        data: null as any
      };
    }
  }

  /**
   * Send payment link via SMS (Physician/Staff action)
   */
  async sendPaymentLinkSms(orderId: string, patientPhone: string): Promise<ApiResponse<any>> {
    return this.makeRequest<any>(`/payments/orders/${orderId}/send-link-sms`, {
      method: 'POST',
      body: JSON.stringify({ patientPhone })
    });
  }

  // TODO: Additional endpoints to implement as needed:
  // - getPaymentStatus(orderId)
  // - processWebhook(payload, signature)
  // - downloadReceipt(paymentLinkId)
  // - emailReceipt(paymentLinkId, email)
}

// Export singleton instance
export const paymentApi = new PaymentApiService();
